import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';
import { useEstimationUnit } from '../../estimation/estimationUnit';
import { EstimationUnitToggle } from '../../estimation/EstimationUnitToggle';

/** Recharts Legend `content` receives a minimal payload-only shape — keep permissive typing. */
function MttrLegendContent(props: {
  payload?: ReadonlyArray<{
    value?: string;
    color?: string;
    dataKey?: string | number;
  }>;
}) {
  const { payload } = props;
  if (!payload?.length) return null;
  const nodes: React.ReactNode[] = [];
  payload.forEach((entry, index) => {
    const dk = String(entry.dataKey ?? '');
    if (dk === 'p1') {
      nodes.push(
        <span key="bug-prio-prefix" className="dynamics-mttr-legend-prio-label">
          Bug priorities:
        </span>
      );
    }
    nodes.push(
      <span key={`${dk}-${index}`} className="dynamics-mttr-legend-entry">
        <span
          className="dynamics-mttr-legend-swatch-line"
          style={{ backgroundColor: entry.color ?? 'currentColor' }}
          aria-hidden
        />
        <span>{entry.value ?? dk}</span>
      </span>
    );
  });
  return <div className="dynamics-mttr-legend-custom">{nodes}</div>;
}

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

/** Series keys in the MTTR points payload that are time-in-hours. Anything
 *  else (e.g. bucket labels) is left untouched by the unit conversion. */
const MTTR_HOUR_KEYS = ['avg', 'p1', 'p2', 'p3', 'p4'] as const;

function MttrChartBody({
  points,
  onPointClick,
}: {
  points: Array<Record<string, number | string | null>>;
  onPointClick?: (bucketLabel: string) => void;
}) {
  const plotH = useDynamicsPlotHeight(320);
  const { unit, suffix, toUnitValue } = useEstimationUnit();
  const isDays = unit === 'days';

  /** Transform every hour-valued series in-place per active unit so Recharts
   *  scales the axis, tooltips, and lines all from the same numbers. */
  const displayPoints = useMemo(
    () =>
      points.map((p) => {
        const next: Record<string, number | string | null> = { ...p };
        for (const k of MTTR_HOUR_KEYS) {
          const v = p[k];
          if (typeof v === 'number') next[k] = toUnitValue(v);
        }
        return next;
      }),
    [points, toUnitValue]
  );

  return (
    <div>
      <ResponsiveContainer width="100%" height={plotH}>
        <LineChart
          data={displayPoints}
          margin={{ ...dynamicsBucketChartMargins }}
          style={onPointClick ? { cursor: 'pointer' } : undefined}
          onClick={(e) => {
            const label = (e as { activeLabel?: string } | null | undefined)?.activeLabel;
            if (onPointClick && typeof label === 'string') onPointClick(label);
          }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
          <XAxis {...dynamicsPeriodAxisProps} />
          <YAxis label={{ value: isDays ? 'days' : 'hours', angle: -90, position: 'insideLeft' }} />
          <Tooltip
            {...DYNAMICS_RECHARTS_TOOLTIP_COMMON}
            formatter={(value) => {
              if (value == null) return 'n/a';
              if (typeof value === 'number') {
                const rounded = isDays ? value.toFixed(1) : Math.round(value);
                return `${rounded} ${suffix}`;
              }
              return String(value);
            }}
          />
          <Legend
            verticalAlign="bottom"
            content={<MttrLegendContent />}
            wrapperStyle={{ paddingTop: 4 }}
          />
          <Line type="monotone" dataKey="avg" name="Avg MTTR" stroke="#0ea5e9" strokeWidth={2} />
          <Line type="monotone" dataKey="p1" name="Highest (P1)" stroke="#ef4444" />
          <Line type="monotone" dataKey="p2" name="High (P2)" stroke="#f97316" />
          <Line type="monotone" dataKey="p3" name="Medium (P3)" stroke="#eab308" />
          <Line type="monotone" dataKey="p4" name="Low (P4)" stroke="#22c55e" />
        </LineChart>
      </ResponsiveContainer>
      <p
        className="dynamics-card__confidence"
        style={{
          marginTop: 6,
          fontSize: 11,
          color: 'var(--color-text-secondary, #888)',
          lineHeight: 1.4,
        }}
      >
        {isDays ? 'Days' : 'Hours'} between <code>created</code> and <code>resolution_date</code>{' '}
        for bugs of each priority resolved in the period. Lower = faster. A missing P1/P2/P3/P4 line
        means no resolved bug of that priority in the period (or bugs had no <code>priority</code>{' '}
        set).
      </p>
    </div>
  );
}

export const DynamicsD_MTTRDynamics: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="D"
      title="MTTR dynamics"
      hint="Mean Time To Resolve for bugs per period, with priority-level series in the legend. Growing MTTR with stable volume = team is drowning."
      needs="Bugs with `created` and `resolution_date` — priorities are optional but power the P1–P4 split."
      metric="mttr"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={320}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.D.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.D.bullets]}
      headerExtra={<EstimationUnitToggle label={null} />}
    >
      {(points, onPointClick) => <MttrChartBody points={points} onPointClick={onPointClick} />}
    </DynamicsChartFrame>
  );
};
